/**
 * La cuenta, al pie del menú lateral.
 *
 * Estaba arriba a la derecha, y era el único inquilino de una franja de 68 px
 * que cruzaba toda la pantalla. En una aplicación contable ese ancho lo quiere
 * la tabla, no el correo del usuario: la sesión se consulta una vez al día y el
 * libro diario se recorre todo el rato.
 *
 * Sigue siendo `<details>`: se abre y se cierra con teclado sin una línea de
 * JavaScript, y no deja un menú colgado si el render falla. Lo único que cambia
 * es que ahora despliega hacia ARRIBA, porque nace del borde inferior.
 */

import type { SessionInfo } from "../api/types";
import { api } from "../api/client";

/** Etiqueta legible de un rol. Un rol desconocido se muestra tal cual. */
const ROLE_LABELS: Record<string, string> = {
  reporting_admin: "Administrador",
  accountant: "Contador",
  auditor: "Auditor",
};

function initials(name: string, email: string): string {
  const base = name.trim() || email.split("@")[0] || "";
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  const letters = parts.length > 1 ? [parts[0][0], parts[1][0]] : [base[0], base[1]];
  return letters.filter(Boolean).join("").toUpperCase() || "?";
}

export function AccountMenu({ session }: { session: SessionInfo }) {
  const email = session.email ?? "";
  const name = session.name ?? email;
  const roles = (session.roles ?? []).map((role) => ROLE_LABELS[role] ?? role);
  const local = session.provider === "local";

  return (
    <details className="account">
      <summary aria-label={`Cuenta de ${name}`} title={`${name} · ${email}`}>
        {session.picture ? (
          <img className="avatar" src={session.picture} alt="" />
        ) : (
          <span className="avatar" aria-hidden="true">
            {initials(name, email)}
          </span>
        )}
        <span className="account-who">
          <span className="account-name">{name}</span>
          <span className="account-role">{roles[0] ?? email}</span>
        </span>
      </summary>

      <div className="account-menu">
        <div className="account-head">
          <strong>{name}</strong>
          <span className="mono small muted">{email}</span>
          {roles.length > 0 && <span className="small">{roles.join(" · ")}</span>}
        </div>
        {local ? (
          <p className="account-note">
            Sesión local de desarrollo, sin proveedor de identidad. No usar en
            producción.
          </p>
        ) : (
          <a className="account-item" href={api.logoutUrl()}>
            Cerrar sesión
          </a>
        )}
      </div>
    </details>
  );
}
